import { useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import { POLL_SLOW } from '@/lib/utils'


/// Total HORIZON a wallet has actually claimed (already landed in their balance), summed across
/// every reward stream - supply/borrow incentives on both HorizonLendingPool reserves, HorizonFarm's
/// LP emissions. Each contract keeps its own running `totalClaimedHorizon(user)`
/// counter, incremented once at claim time and never reset (see HorizonLendingPool.sol's own doc
/// comment on that field) - this is a live read, not the currently-pending/unclaimed amount
/// `useProtocolState`'s own `pendingSupplyHorizon`/`pendingBorrowHorizon`/farm `pendingHorizon`/
/// `userStakingPendingHorizon` fields track.
///
/// Previously reconstructed from claim event logs (a client-side `getLogs` scan across 3
/// contracts and 4 event types) - replaced once the counters shipped, since a live multicall read
/// is both simpler and doesn't share the log-scanning approach's known scaling limitation against
/// a real mainnet deployment's history (see useAllBorrowers/useUserPortfolioHistory, which still
/// use getLogsChunked for the cases a running on-chain counter can't cover).
export function useTotalClaimedHorizon(user) {
  const enabled = IS_LIVE && !!user

  const { data, isLoading } = useReadContracts({
    contracts: [
      { address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'totalClaimedHorizon', args: [user ?? ZERO_ADDR] },
      { address: ADDRESSES.HORIZON_EMISSIONS, abi: farmAbi, functionName: 'totalClaimedHorizon', args: [user ?? ZERO_ADDR] },
    ],
    query: { enabled, refetchInterval: POLL_SLOW },
  })

  if (!enabled) return { totalClaimedHorizon: 0, loading: false }

  const sumWei = (data ?? []).reduce((sum, r) => sum + (r?.result ?? 0n), 0n)
  return { totalClaimedHorizon: Number(sumWei) / 1e18, loading: isLoading }
}
